type TrackingValue = string | number | boolean | null | undefined

export type TrackingParams = Record<string, TrackingValue>

export type AttributionTouch = {
  source: string
  medium: string
  campaign: string
  campaignId: string
  term: string
  content: string
  sourcePlatform: string
  landingPage: string
  referrer: string
  capturedAt: string
  gclid: string
  gbraid: string
  wbraid: string
  fbclid: string
  ttclid: string
  msclkid: string
}

export type AttributionState = {
  version: 1
  visitorId: string
  firstTouch: AttributionTouch
  lastTouch: AttributionTouch
}

const STORAGE_KEY = 'inanc_attribution_v1'
const STORAGE_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000
const PII_KEYS = new Set([
  'email', 'mail', 'phone', 'mobile', 'name', 'surname', 'full_name', 'first_name', 'last_name',
  'tc', 'tckn', 'passport', 'passport_no', 'customer_email', 'customer_phone', 'customer_mobile',
  'customer_name', 'user_email', 'user_phone', 'responsible_email', 'responsible_mobile', 'responsible_name'
])
const CONSENT_DENIED_KEYS = new Set([...PII_KEYS, 'attribution_id', 'call_id', 'lead_id'])
const AI_HOSTS = ['chatgpt.com', 'chat.openai.com', 'perplexity.ai', 'gemini.google.com', 'claude.ai', 'copilot.microsoft.com']
const SOCIAL_HOSTS = ['facebook.com', 'instagram.com', 't.co', 'twitter.com', 'x.com', 'linkedin.com', 'tiktok.com', 'youtube.com']
const SEARCH_HOSTS: Record<string, string> = {
  'google.': 'google',
  'bing.com': 'bing',
  'yandex.': 'yandex',
  'yahoo.': 'yahoo',
  'duckduckgo.com': 'duckduckgo'
}

let memoryState: AttributionState | null = null

const isPiiKey = (key: string) => PII_KEYS.has(key.toLowerCase())

const newId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const safeHost = (value: string) => {
  if (!value) return ''
  try {
    return new URL(value).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    return ''
  }
}

const cleanPageUrl = (value: string) => {
  try {
    const url = new URL(value)
    for (const key of [...url.searchParams.keys()]) {
      if (isPiiKey(key)) url.searchParams.delete(key)
    }
    return `${url.pathname}${url.search}`
  } catch {
    return '/'
  }
}

const cleanPageLocation = (value: string) => {
  try {
    const url = new URL(value)
    return `${url.origin}${cleanPageUrl(value)}`
  } catch {
    return cleanPageUrl(value)
  }
}

const getParam = (url: URL, key: string) => (url.searchParams.get(key) || '').slice(0, 250)

export const buildAttributionTouch = (locationHref: string, documentReferrer = '', capturedAt = new Date().toISOString()): AttributionTouch => {
  const url = new URL(locationHref, 'https://inanctur.com.tr')
  const referrerHost = safeHost(documentReferrer)
  const currentHost = safeHost(url.href)
  const externalReferrer = referrerHost && referrerHost !== currentHost ? referrerHost : ''
  const utmSource = getParam(url, 'utm_source').toLowerCase()
  const utmMedium = getParam(url, 'utm_medium').toLowerCase()
  const gclid = getParam(url, 'gclid')
  const gbraid = getParam(url, 'gbraid')
  const wbraid = getParam(url, 'wbraid')
  const fbclid = getParam(url, 'fbclid')
  let source = utmSource
  let medium = utmMedium

  if (!source && (gclid || gbraid || wbraid)) {
    source = 'google'
    medium = 'cpc'
  }

  if (!source && externalReferrer) {
    const searchSource = Object.entries(SEARCH_HOSTS).find(([part]) => externalReferrer.includes(part))?.[1]
    source = searchSource || externalReferrer
    if (searchSource) medium = 'organic'
    else if (AI_HOSTS.some(host => externalReferrer === host || externalReferrer.endsWith(`.${host}`))) medium = 'ai_referral'
    else if (SOCIAL_HOSTS.some(host => externalReferrer === host || externalReferrer.endsWith(`.${host}`))) medium = 'organic_social'
    else medium = 'referral'
  }

  if (!source) {
    source = '(direct)'
    medium = '(none)'
  }

  return {
    source,
    medium: medium || '(none)',
    campaign: getParam(url, 'utm_campaign'),
    campaignId: getParam(url, 'utm_id'),
    term: getParam(url, 'utm_term'),
    content: getParam(url, 'utm_content'),
    sourcePlatform: getParam(url, 'utm_source_platform'),
    landingPage: cleanPageUrl(url.href),
    referrer: externalReferrer,
    capturedAt,
    gclid,
    gbraid,
    wbraid,
    fbclid,
    ttclid: getParam(url, 'ttclid'),
    msclkid: getParam(url, 'msclkid')
  }
}

const hasAnalyticsConsent = () => {
  if (typeof document === 'undefined') return false
  return document.cookie.split(';').some(value => value.trim() === 'cookie_consent=accepted')
}

const readStoredState = (): AttributionState | null => {
  if (typeof localStorage === 'undefined' || !hasAnalyticsConsent()) return null
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') as AttributionState | null
    const capturedAt = Date.parse(parsed?.lastTouch?.capturedAt || '')
    if (!parsed || parsed.version !== 1 || !Number.isFinite(capturedAt) || Date.now() - capturedAt > STORAGE_MAX_AGE_MS) return null
    return parsed
  } catch {
    return null
  }
}

const persistState = (state: AttributionState) => {
  if (typeof localStorage === 'undefined' || !hasAnalyticsConsent()) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Measurement must never interrupt the reservation flow.
  }
}

const getState = () => {
  if (typeof window === 'undefined') return null
  const current = buildAttributionTouch(window.location.href, document.referrer)
  const inMemory = memoryState
  const stored = inMemory || readStoredState()

  if (!stored) {
    memoryState = { version: 1, visitorId: newId(), firstTouch: current, lastTouch: current }
  } else {
    const hasClickOrCampaign = Boolean(
      current.campaign || current.campaignId || current.gclid || current.gbraid || current.wbraid
      || current.fbclid || current.ttclid || current.msclkid
    )
    const hasNewCampaign = current.source !== '(direct)' && (!inMemory || hasClickOrCampaign)
    memoryState = { ...stored, lastTouch: hasNewCampaign ? current : stored.lastTouch }
  }

  persistState(memoryState)
  return memoryState
}

const sanitizeParams = (params: TrackingParams, analyticsConsentGranted = true) => Object.fromEntries(
  Object.entries(params)
    .filter(([key, value]) => (
      !isPiiKey(key)
      && (analyticsConsentGranted || !CONSENT_DENIED_KEYS.has(key.toLowerCase()))
      && value !== undefined
      && value !== null
      && value !== ''
    ))
    .map(([key, value]) => [key.slice(0, 40), typeof value === 'string' ? value.slice(0, 100) : value])
)

export const useTracking = () => {
  const refreshAttribution = () => getState()

  const clearAttribution = () => {
    memoryState = null
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage may be unavailable in private browsing modes.
    }
  }

  const getAttributionPayload = () => {
    const state = getState()
    if (!state || !hasAnalyticsConsent()) return null
    return {
      attribution_id: state.visitorId,
      first_source: state.firstTouch.source,
      first_medium: state.firstTouch.medium,
      first_campaign: state.firstTouch.campaign,
      first_campaign_id: state.firstTouch.campaignId,
      first_term: state.firstTouch.term,
      first_content: state.firstTouch.content,
      first_source_platform: state.firstTouch.sourcePlatform,
      first_captured_at: state.firstTouch.capturedAt,
      last_source: state.lastTouch.source,
      last_medium: state.lastTouch.medium,
      last_campaign: state.lastTouch.campaign,
      last_campaign_id: state.lastTouch.campaignId,
      last_term: state.lastTouch.term,
      last_content: state.lastTouch.content,
      last_source_platform: state.lastTouch.sourcePlatform,
      last_captured_at: state.lastTouch.capturedAt,
      landing_page: state.firstTouch.landingPage,
      referrer: state.lastTouch.referrer || state.firstTouch.referrer,
      gclid: state.lastTouch.gclid || state.firstTouch.gclid,
      gbraid: state.lastTouch.gbraid || state.firstTouch.gbraid,
      wbraid: state.lastTouch.wbraid || state.firstTouch.wbraid,
      fbclid: state.lastTouch.fbclid || state.firstTouch.fbclid,
      ttclid: state.lastTouch.ttclid || state.firstTouch.ttclid,
      msclkid: state.lastTouch.msclkid || state.firstTouch.msclkid,
      consent_granted: true
    }
  }

  const trackEvent = (event: string, params: TrackingParams = {}) => {
    if (typeof window === 'undefined') return false
    const analyticsConsentGranted = hasAnalyticsConsent()
    const attribution = analyticsConsentGranted ? getAttributionPayload() : null
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: event.replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 40),
      ...sanitizeParams(params, analyticsConsentGranted),
      ...(attribution || {}),
      consent_granted: analyticsConsentGranted
    })
    return true
  }

  const trackPageView = () => trackEvent('page_view', {
    page_path: cleanPageUrl(window.location.href),
    page_title: document.title,
    page_location: cleanPageLocation(window.location.href)
  })

  return { clearAttribution, refreshAttribution, getAttributionPayload, trackEvent, trackPageView }
}

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown> | IArguments>
    gtag?: (...args: unknown[]) => void
  }
}
